/**
 * Cost Model
 *
 * Computes annual running costs and itemised upgrade penalties per option.
 *
 * For every option this module produces:
 *   - bestCase annual running cost (all upgrades applied)
 *   - baseline/no-upgrades annual running cost
 *   - itemised penalty items for skipping each upgrade
 *   - tariff scenarios (known + standard + heat_pump + tou)
 *   - debug source trace
 *
 * Heat demand priority:
 *   1. heatDemand.annualSpaceHeatDemandKwh (canonical field)
 *   2. heatDemand.designHeatLossKw  → seasonal model
 *   3. actualAnnualHeatKwh (legacy)
 *   4. simple property model (fallback)
 */

import type { SystemRecInput, RecommendationOption } from '../types';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface TariffScenario {
  key: 'known' | 'standard' | 'heat_pump' | 'tou';
  label: string;
  gas?: { unitRatePPerKwh: number };
  electric?: { unitRatePPerKwh: number };
}

export interface CostRange {
  low: number;
  mid: number;
  high: number;
}

export interface PenaltyItem {
  key:
    | 'skip_flush'
    | 'skip_smart_controls'
    | 'skip_emitter_upgrades'
    | 'skip_weather_comp'
    | 'skip_commissioning';
  label: string;
  impact: {
    annualCostDeltaGBP: number;
    energyDeltaKwh?: number;
    notes: string[];
  };
  severity: 'minor' | 'moderate' | 'major' | 'viability';
}

export interface BestCaseRequirement {
  key: string;
  label: string;
  why: string;
}

export interface OptionCosting {
  annualRunningCostGBP: {
    bestCase: CostRange;
    baseline: CostRange;
  };
  tariffScenarios: TariffScenario[];
  bestCaseRequirements: BestCaseRequirement[];
  penaltyItems: PenaltyItem[];
  confidence: 'low' | 'moderate' | 'high';
  tightenConfidence: string[];
  debug: {
    usedHeatDemandSource:
      | 'annualSpaceHeatDemandKwh'
      | 'designHeatLossKw'
      | 'actualAnnualHeatKwh'
      | 'derived';
    spaceHeatKwh: number;
    dhwKwh: number;
    tariffUsed: 'known' | 'standard';
  };
}

// ─── Default tariffs (UK typical, pence/kWh) ─────────────────────────────────

const DEFAULT_GAS_UNIT_RATE_P_PER_KWH = 6.0;   // ~6p/kWh typical UK
const DEFAULT_ELEC_UNIT_RATE_P_PER_KWH = 24.5;  // ~24.5p/kWh SVT
const HEAT_PUMP_TARIFF_P_PER_KWH = 20.0;         // Discounted HP tariff
const TOU_TARIFF_P_PER_KWH = 17.0;               // Off-peak TOU assumption

// ─── Physical limits ──────────────────────────────────────────────────────────

/** SPF cannot fall below 1.0 — a system delivering less energy than it consumes is physically impossible */
const MIN_SPF_THRESHOLD = 1.0;
/** Even a poorly maintained boiler in worst-case operating conditions achieves ~50% efficiency */
const MIN_BOILER_EFFICIENCY = 0.5;
/** SPF below this threshold is considered non-viable for a heat pump installation */
const MIN_VIABLE_SPF = 2.0;

// ─── Seasonal degree-day model (simplified, UK average) ──────────────────────

/**
 * Derive annual space heat kWh from design heat loss kW using a simple
 * degree-day / full-load-hours model.
 *
 * Source: CIBSE Guide A (UK climate data).
 * UK average: ~1900 heating degree days (base 15.5°C), design ΔT = 24 K
 * Full load hours ≈ (HDD × 24) / ΔT
 */
function designKwToAnnualKwh(designHeatLossKw: number): number {
  const heatingDegreeDays = 1900; // UK average
  const designDeltaT = 24;       // 21°C internal - (-3°C) external
  const fullLoadHours = (heatingDegreeDays * 24) / designDeltaT;
  return Math.round(designHeatLossKw * fullLoadHours);
}

// ─── Simple property-based fallback ──────────────────────────────────────────

function propertyFallbackSpaceHeatKwh(input: SystemRecInput): number {
  const type = input.propertyType || 'semi';
  const bases: Record<string, number> = {
    flat: 7200,
    bungalow: 7200,
    terraced: 8800,
    end_terrace: 9500,
    mid_terrace: 8500,
    semi: 10000,
    semi_detached: 10000,
    detached: 12800,
  };
  const base = bases[type] ?? 10000;
  const insulMult =
    input.insulationLevel === 'high' ? 0.80
    : input.insulationLevel === 'poor' ? 1.20
    : 1.00;
  return Math.round(base * insulMult);
}

// ─── Determine heat demand ────────────────────────────────────────────────────

interface HeatDemandResult {
  spaceHeatKwh: number;
  dhwKwh: number;
  source: OptionCosting['debug']['usedHeatDemandSource'];
}

function resolveHeatDemand(input: SystemRecInput): HeatDemandResult {
  const hd = input.heatDemand;

  // Priority 1: canonical annualSpaceHeatDemandKwh
  if (hd?.annualSpaceHeatDemandKwh && hd.annualSpaceHeatDemandKwh > 0) {
    const dhw = hd.annualDhwDemandKwh ?? estimateDhwKwh(input);
    return {
      spaceHeatKwh: hd.annualSpaceHeatDemandKwh,
      dhwKwh: dhw,
      source: 'annualSpaceHeatDemandKwh',
    };
  }

  // Priority 2: designHeatLossKw → seasonal model
  if (hd?.designHeatLossKw && hd.designHeatLossKw > 0) {
    const spaceHeatKwh = designKwToAnnualKwh(hd.designHeatLossKw);
    const dhw = hd.annualDhwDemandKwh ?? estimateDhwKwh(input);
    return { spaceHeatKwh, dhwKwh: dhw, source: 'designHeatLossKw' };
  }

  // Priority 3: legacy actualAnnualHeatKwh
  if (input.actualAnnualHeatKwh && input.actualAnnualHeatKwh > 0) {
    const dhwRatio = 0.20;
    const spaceHeatKwh = Math.round(input.actualAnnualHeatKwh * (1 - dhwRatio));
    const dhw = Math.round(input.actualAnnualHeatKwh * dhwRatio);
    return { spaceHeatKwh, dhwKwh: dhw, source: 'actualAnnualHeatKwh' };
  }

  // Priority 4: calibrate against gas consumption if available
  if (input.consumption?.annualGasKwh && input.consumption.annualGasKwh > 0) {
    // Boiler seasonal efficiency ~85%; subtract estimated cooking gas usage (~500 kWh/yr, DECC/BEIS estimate)
    const cooking = 500;
    const deliveredHeat = Math.round(
      (input.consumption.annualGasKwh - cooking) * 0.85,
    );
    const dhwKwh = estimateDhwKwh(input);
    const spaceHeatKwh = Math.max(0, deliveredHeat - dhwKwh);
    return { spaceHeatKwh, dhwKwh, source: 'derived' };
  }

  // Fallback: property-type model
  const spaceHeatKwh = propertyFallbackSpaceHeatKwh(input);
  const dhwKwh = estimateDhwKwh(input);
  return { spaceHeatKwh, dhwKwh, source: 'derived' };
}

function estimateDhwKwh(input: SystemRecInput): number {
  if (input.heatDemand?.annualDhwDemandKwh) return input.heatDemand.annualDhwDemandKwh;
  const base = input.hotWaterDemand === 'low' ? 1800
    : input.hotWaterDemand === 'high' ? 3200
    : 2400;
  const occupancy = input.occupancy ?? 2.5;
  return Math.round(base + (occupancy - 2.5) * 600);
}

// ─── Tariff helpers ───────────────────────────────────────────────────────────

function buildTariffScenarios(input: SystemRecInput): TariffScenario[] {
  const scenarios: TariffScenario[] = [];

  const knownGas = input.tariffs?.gas?.unitRatePPerKwh;
  const knownElec = input.tariffs?.electric?.unitRatePPerKwh;

  if (knownGas !== undefined || knownElec !== undefined) {
    scenarios.push({
      key: 'known',
      label: 'Your tariff',
      ...(knownGas !== undefined ? { gas: { unitRatePPerKwh: knownGas } } : {}),
      ...(knownElec !== undefined ? { electric: { unitRatePPerKwh: knownElec } } : {}),
    });
  }

  scenarios.push({
    key: 'standard',
    label: 'Typical SVT',
    gas: { unitRatePPerKwh: DEFAULT_GAS_UNIT_RATE_P_PER_KWH },
    electric: { unitRatePPerKwh: DEFAULT_ELEC_UNIT_RATE_P_PER_KWH },
  });

  scenarios.push({
    key: 'heat_pump',
    label: 'Heat pump tariff',
    electric: { unitRatePPerKwh: HEAT_PUMP_TARIFF_P_PER_KWH },
  });

  scenarios.push({
    key: 'tou',
    label: 'Time-of-use tariff (off-peak)',
    electric: { unitRatePPerKwh: TOU_TARIFF_P_PER_KWH },
  });

  return scenarios;
}

function resolveElecRate(input: SystemRecInput, scenario: 'known' | 'standard'): number {
  if (scenario === 'known' && input.tariffs?.electric?.unitRatePPerKwh) {
    return input.tariffs.electric.unitRatePPerKwh;
  }
  return DEFAULT_ELEC_UNIT_RATE_P_PER_KWH;
}

function resolveGasRate(input: SystemRecInput, scenario: 'known' | 'standard'): number {
  if (scenario === 'known' && input.tariffs?.gas?.unitRatePPerKwh) {
    return input.tariffs.gas.unitRatePPerKwh;
  }
  return DEFAULT_GAS_UNIT_RATE_P_PER_KWH;
}

// ─── SPF helpers ──────────────────────────────────────────────────────────────

/**
 * Estimate base SPF for a heat pump option from emitter type and insulation.
 * Ranges informed by UK field data (BEIS Heat Pump Field Trials 2013–2015
 * and MCS heat pump performance data 2020–2023).
 */
function estimateBaseSPF(
  option: RecommendationOption,
  input: SystemRecInput,
): { low: number; mid: number; high: number } {
  const emitterType = input.emitters?.type ?? 'radiators';
  const isGSHP = option.boiler === 'ground_source_hp';

  // UFH achieves lower flow temps → higher SPF
  if (emitterType === 'underfloor') {
    return isGSHP ? { low: 3.4, mid: 3.8, high: 4.4 } : { low: 3.0, mid: 3.4, high: 3.8 };
  }
  // Oversized radiators — moderate improvement
  if (emitterType === 'radiators' && input.emitters?.lowTempSuitability === 'likely_ok') {
    return isGSHP ? { low: 2.9, mid: 3.3, high: 3.8 } : { low: 2.6, mid: 3.0, high: 3.5 };
  }
  // Standard radiators (may need upgrade)
  return isGSHP ? { low: 2.6, mid: 3.0, high: 3.5 } : { low: 2.3, mid: 2.7, high: 3.2 };
}

// ─── Boiler seasonal efficiency ───────────────────────────────────────────────

/**
 * Estimate seasonal efficiency for a gas/oil boiler option.
 */
function estimateBoilerSeasonalEfficiency(option: RecommendationOption): {
  low: number;
  mid: number;
  high: number;
} {
  const boiler = option.boiler;
  if (boiler === 'combi' || boiler === 'system' || boiler === 'regular') {
    return { low: 0.86, mid: 0.89, high: 0.92 };
  }
  if (boiler === 'storage_combi') {
    return { low: 0.80, mid: 0.84, high: 0.88 };
  }
  return { low: 0.84, mid: 0.87, high: 0.90 };
}

// ─── Running cost calculations ────────────────────────────────────────────────

function calcBoilerAnnualCost(
  spaceHeatKwh: number,
  dhwKwh: number,
  seasonalEff: number,
  gasRateP: number,
): number {
  const usefulHeat = spaceHeatKwh + dhwKwh;
  const inputGas = usefulHeat / seasonalEff;
  return Math.round((inputGas * gasRateP) / 100);
}

function calcHpAnnualCost(
  spaceHeatKwh: number,
  dhwKwh: number,
  spf: number,
  elecRateP: number,
): number {
  const usefulHeat = spaceHeatKwh + dhwKwh;
  const inputElec = usefulHeat / spf;
  return Math.round((inputElec * elecRateP) / 100);
}

// ─── Penalty items ────────────────────────────────────────────────────────────

function buildBoilerPenalties(
  spaceHeatKwh: number,
  dhwKwh: number,
  gasRateP: number,
  bestCaseEff: { low: number; mid: number; high: number },
): PenaltyItem[] {
  const items: PenaltyItem[] = [];

  // Flush / dirty system: ~3% seasonal efficiency penalty
  const noFlushEff = {
    low: bestCaseEff.low - 0.03,
    mid: bestCaseEff.mid - 0.03,
    high: bestCaseEff.high - 0.03,
  };
  const noFlushDeltaGBP =
    calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, noFlushEff.mid, gasRateP) -
    calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, bestCaseEff.mid, gasRateP);
  const usefulHeat = spaceHeatKwh + dhwKwh;
  const noFlushDeltaKwh = Math.round(
    usefulHeat / noFlushEff.mid - usefulHeat / bestCaseEff.mid,
  );
  items.push({
    key: 'skip_flush',
    label: 'Skip system flush / powerflush',
    impact: {
      annualCostDeltaGBP: Math.round(noFlushDeltaGBP),
      energyDeltaKwh: noFlushDeltaKwh,
      notes: [
        'Dirty system magnetite and sludge reduces heat transfer and boiler efficiency.',
        '~3% reduction in seasonal efficiency modelled.',
      ],
    },
    severity: noFlushDeltaGBP > 60 ? 'moderate' : 'minor',
  });

  // Smart controls (weather/load compensation): ~5% seasonal efficiency gain
  const noSmartEff = {
    low: bestCaseEff.low - 0.05,
    mid: bestCaseEff.mid - 0.05,
    high: bestCaseEff.high - 0.05,
  };
  const noSmartDeltaGBP =
    calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, noSmartEff.mid, gasRateP) -
    calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, bestCaseEff.mid, gasRateP);
  const noSmartDeltaKwh = Math.round(
    usefulHeat / noSmartEff.mid - usefulHeat / bestCaseEff.mid,
  );
  items.push({
    key: 'skip_smart_controls',
    label: 'Skip smart controls / weather compensation',
    impact: {
      annualCostDeltaGBP: Math.round(noSmartDeltaGBP),
      energyDeltaKwh: noSmartDeltaKwh,
      notes: [
        'Weather or load compensation reduces flow temperature and cycling losses.',
        '~5% reduction in seasonal efficiency modelled.',
      ],
    },
    severity: noSmartDeltaGBP > 80 ? 'moderate' : 'minor',
  });

  // Commissioning / balancing: ~2% seasonal efficiency penalty
  const noCommEff = {
    low: bestCaseEff.low - 0.02,
    mid: bestCaseEff.mid - 0.02,
    high: bestCaseEff.high - 0.02,
  };
  const noCommDeltaGBP =
    calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, noCommEff.mid, gasRateP) -
    calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, bestCaseEff.mid, gasRateP);
  items.push({
    key: 'skip_commissioning',
    label: 'Skip proper commissioning / balancing',
    impact: {
      annualCostDeltaGBP: Math.round(noCommDeltaGBP),
      notes: ['Poor balancing causes some radiators to over- or under-heat.'],
    },
    severity: 'minor',
  });

  return items;
}

function buildHeatPumpPenalties(
  spaceHeatKwh: number,
  dhwKwh: number,
  elecRateP: number,
  bestCaseSpf: { low: number; mid: number; high: number },
  option: RecommendationOption,
  input: SystemRecInput,
): PenaltyItem[] {
  const items: PenaltyItem[] = [];

  const usefulHeat = spaceHeatKwh + dhwKwh;
  const emitterNeedsUpgrade =
    input.emitters?.lowTempSuitability === 'definitely_needs_upgrade' ||
    input.emitters?.lowTempSuitability === 'likely_needs_upgrade';

  // Emitter upgrades (HP specific — biggest lever)
  if (emitterNeedsUpgrade) {
    // Without emitter upgrades, flow temp is higher → SPF degrades significantly
    const noEmitterSpf = {
      low: bestCaseSpf.low - 0.5,
      mid: bestCaseSpf.mid - 0.6,
      high: bestCaseSpf.high - 0.6,
    };
    const noEmitterDeltaGBP =
      calcHpAnnualCost(spaceHeatKwh, dhwKwh, noEmitterSpf.mid, elecRateP) -
      calcHpAnnualCost(spaceHeatKwh, dhwKwh, bestCaseSpf.mid, elecRateP);
    const noEmitterDeltaKwh = Math.round(
      usefulHeat / noEmitterSpf.mid - usefulHeat / bestCaseSpf.mid,
    );

    // Determine if viability is at risk (SPF below minimum viable threshold is generally non-viable)
    const isViabilityRisk = noEmitterSpf.mid < MIN_VIABLE_SPF;
    items.push({
      key: 'skip_emitter_upgrades',
      label: 'Skip emitter upgrades (radiator upsizing)',
      impact: {
        annualCostDeltaGBP: Math.round(noEmitterDeltaGBP),
        energyDeltaKwh: noEmitterDeltaKwh,
        notes: [
          isViabilityRisk
            ? 'Without emitter upgrades, required flow temperature may exceed heat pump design range — system viability at risk.'
            : 'Higher flow temperature required, reducing SPF significantly.',
          `SPF modelled at ${noEmitterSpf.mid.toFixed(1)} without upgrades vs ${bestCaseSpf.mid.toFixed(1)} best case.`,
        ],
      },
      severity: isViabilityRisk ? 'viability' : 'major',
    });
  } else {
    // Minor penalty even when emitters are adequate (e.g., optimisation opportunity)
    const noEmitterSpf = {
      low: bestCaseSpf.low - 0.15,
      mid: bestCaseSpf.mid - 0.2,
      high: bestCaseSpf.high - 0.2,
    };
    const noEmitterDeltaGBP =
      calcHpAnnualCost(spaceHeatKwh, dhwKwh, noEmitterSpf.mid, elecRateP) -
      calcHpAnnualCost(spaceHeatKwh, dhwKwh, bestCaseSpf.mid, elecRateP);
    items.push({
      key: 'skip_emitter_upgrades',
      label: 'Skip emitter optimisation',
      impact: {
        annualCostDeltaGBP: Math.round(noEmitterDeltaGBP),
        notes: ['Minor SPF improvement possible with optimised emitters.'],
      },
      severity: 'minor',
    });
  }

  // Weather compensation / smart controls: ~0.3 SPF penalty
  const noWeatherSpf = {
    low: bestCaseSpf.low - 0.2,
    mid: bestCaseSpf.mid - 0.3,
    high: bestCaseSpf.high - 0.3,
  };
  const noWeatherDeltaGBP =
    calcHpAnnualCost(spaceHeatKwh, dhwKwh, noWeatherSpf.mid, elecRateP) -
    calcHpAnnualCost(spaceHeatKwh, dhwKwh, bestCaseSpf.mid, elecRateP);
  const noWeatherDeltaKwh = Math.round(
    usefulHeat / noWeatherSpf.mid - usefulHeat / bestCaseSpf.mid,
  );
  items.push({
    key: 'skip_weather_comp',
    label: 'Skip weather compensation / smart controls',
    impact: {
      annualCostDeltaGBP: Math.round(noWeatherDeltaGBP),
      energyDeltaKwh: noWeatherDeltaKwh,
      notes: [
        'Weather compensation allows heat pump to modulate to outside temperature.',
        '~0.3 SPF reduction modelled without this.',
      ],
    },
    severity: noWeatherDeltaGBP > 100 ? 'major' : 'moderate',
  });

  // Flush / clean system: ~0.1 SPF penalty
  const noFlushSpf = {
    low: bestCaseSpf.low - 0.1,
    mid: bestCaseSpf.mid - 0.1,
    high: bestCaseSpf.high - 0.1,
  };
  const noFlushDeltaGBP =
    calcHpAnnualCost(spaceHeatKwh, dhwKwh, noFlushSpf.mid, elecRateP) -
    calcHpAnnualCost(spaceHeatKwh, dhwKwh, bestCaseSpf.mid, elecRateP);
  items.push({
    key: 'skip_flush',
    label: 'Skip system flush before heat pump installation',
    impact: {
      annualCostDeltaGBP: Math.round(noFlushDeltaGBP),
      notes: [
        'Contaminated system reduces heat exchanger efficiency and can void warranty.',
        '~0.1 SPF reduction modelled.',
      ],
    },
    severity: noFlushDeltaGBP > 60 ? 'moderate' : 'minor',
  });

  // Commissioning / balancing
  const noCommSpf = {
    low: bestCaseSpf.low - 0.1,
    mid: bestCaseSpf.mid - 0.15,
    high: bestCaseSpf.high - 0.15,
  };
  const noCommDeltaGBP =
    calcHpAnnualCost(spaceHeatKwh, dhwKwh, noCommSpf.mid, elecRateP) -
    calcHpAnnualCost(spaceHeatKwh, dhwKwh, bestCaseSpf.mid, elecRateP);
  items.push({
    key: 'skip_commissioning',
    label: 'Skip commissioning / hydraulic balancing',
    impact: {
      annualCostDeltaGBP: Math.round(noCommDeltaGBP),
      notes: ['Poor balancing causes some circuits to over-pump; reduces SPF.'],
    },
    severity: 'minor',
  });

  return items;
}

// ─── Best-case requirements ───────────────────────────────────────────────────

function buildBestCaseRequirements(
  option: RecommendationOption,
  input: SystemRecInput,
): BestCaseRequirement[] {
  const reqs: BestCaseRequirement[] = [];
  const isHeatPump =
    option.systemType === 'heat_pump' ||
    option.boiler === 'air_source_hp' ||
    option.boiler === 'ground_source_hp';

  reqs.push({
    key: 'flush',
    label: 'System flush / powerflush',
    why: isHeatPump
      ? 'Clean system required for heat pump efficiency and warranty.'
      : 'Removes magnetite/sludge that impairs boiler heat transfer.',
  });

  reqs.push({
    key: 'smart_controls',
    label: isHeatPump ? 'Weather compensation enabled' : 'Smart thermostat / load compensation',
    why: isHeatPump
      ? 'Allows heat pump to run at lowest possible flow temperature year-round.'
      : 'Reduces boiler cycling and modulates output to actual load.',
  });

  if (isHeatPump) {
    const emitterSuit = input.emitters?.lowTempSuitability;
    if (
      emitterSuit === 'definitely_needs_upgrade' ||
      emitterSuit === 'likely_needs_upgrade' ||
      emitterSuit === 'unknown' ||
      !emitterSuit
    ) {
      reqs.push({
        key: 'emitter_upgrades',
        label: 'Emitter upgrades (radiator upsizing or UFH)',
        why: 'Required to achieve design flow temperature ≤ 45–50°C for efficient heat pump operation.',
      });
    }
  }

  reqs.push({
    key: 'commissioning',
    label: 'Full commissioning and hydraulic balancing',
    why: 'Ensures all emitters receive correct flow; prevents short-cycling and hot/cold spots.',
  });

  return reqs;
}

// ─── Confidence assessment ────────────────────────────────────────────────────

function assessConfidence(
  demandSource: OptionCosting['debug']['usedHeatDemandSource'],
  hasTariffs: boolean,
): { confidence: OptionCosting['confidence']; tighten: string[] } {
  const tighten: string[] = [];

  if (demandSource === 'derived') {
    tighten.push('Provide actual annual gas kWh from bills or smart meter for more accurate running costs.');
  }
  if (demandSource !== 'annualSpaceHeatDemandKwh' && demandSource !== 'designHeatLossKw') {
    tighten.push('Complete a heat loss calculation to get a precise annual space heating demand figure.');
  }
  if (!hasTariffs) {
    tighten.push('Enter your actual gas and electricity tariff unit rates for accurate cost figures.');
  }

  const confidence =
    demandSource === 'annualSpaceHeatDemandKwh' && hasTariffs ? 'high'
    : demandSource === 'designHeatLossKw' || demandSource === 'actualAnnualHeatKwh' ? 'moderate'
    : 'low';

  return { confidence, tighten };
}

// ─── Main entry point ─────────────────────────────────────────────────────────

/**
 * Compute running-cost costing data for a single option.
 *
 * @param option - The recommendation option to cost
 * @param input  - The full survey input (EngineInput)
 * @returns OptionCosting — best case, baseline, tariff scenarios, penalty items
 */
export function computeOptionCosting(
  option: RecommendationOption,
  input: SystemRecInput,
): OptionCosting {
  const isHeatPump =
    option.systemType === 'heat_pump' ||
    option.boiler === 'air_source_hp' ||
    option.boiler === 'ground_source_hp';

  // Resolve heat demand
  const { spaceHeatKwh, dhwKwh, source } = resolveHeatDemand(input);

  // Resolve tariffs
  const tariffScenario = input.tariffs?.gas || input.tariffs?.electric ? 'known' : 'standard';
  const gasRateP = resolveGasRate(input, tariffScenario);
  const elecRateP = resolveElecRate(input, tariffScenario);

  let bestCaseCost: CostRange;
  let baselineCost: CostRange;
  let penaltyItems: PenaltyItem[];

  if (isHeatPump) {
    const baseSPF = estimateBaseSPF(option, input);

    // Best case: all upgrades applied → use mid SPF
    bestCaseCost = {
      low: calcHpAnnualCost(spaceHeatKwh, dhwKwh, baseSPF.high, elecRateP),
      mid: calcHpAnnualCost(spaceHeatKwh, dhwKwh, baseSPF.mid, elecRateP),
      high: calcHpAnnualCost(spaceHeatKwh, dhwKwh, baseSPF.low, elecRateP),
    };

    // Baseline (no upgrades): emitter penalty + weather comp penalty applied
    const emitterNeedsUpgrade =
      input.emitters?.lowTempSuitability === 'definitely_needs_upgrade' ||
      input.emitters?.lowTempSuitability === 'likely_needs_upgrade';
    const baselineSPFReduction = (emitterNeedsUpgrade ? 0.6 : 0.2) + 0.3;
    const baselineSPF = {
      low: baseSPF.low - baselineSPFReduction,
      mid: baseSPF.mid - baselineSPFReduction,
      high: baseSPF.high - baselineSPFReduction,
    };
    baselineCost = {
      low: calcHpAnnualCost(spaceHeatKwh, dhwKwh, Math.max(MIN_SPF_THRESHOLD, baselineSPF.high), elecRateP),
      mid: calcHpAnnualCost(spaceHeatKwh, dhwKwh, Math.max(MIN_SPF_THRESHOLD, baselineSPF.mid), elecRateP),
      high: calcHpAnnualCost(spaceHeatKwh, dhwKwh, Math.max(MIN_SPF_THRESHOLD, baselineSPF.low), elecRateP),
    };

    penaltyItems = buildHeatPumpPenalties(spaceHeatKwh, dhwKwh, elecRateP, baseSPF, option, input);
  } else {
    // Gas/oil boiler path
    const baseEff = estimateBoilerSeasonalEfficiency(option);

    bestCaseCost = {
      low: calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, baseEff.high, gasRateP),
      mid: calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, baseEff.mid, gasRateP),
      high: calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, baseEff.low, gasRateP),
    };

    // Baseline: flush + smart controls penalties applied
    const baselineEff = {
      low: baseEff.low - 0.08,
      mid: baseEff.mid - 0.08,
      high: baseEff.high - 0.08,
    };
    baselineCost = {
      low: calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, Math.max(MIN_BOILER_EFFICIENCY, baselineEff.high), gasRateP),
      mid: calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, Math.max(MIN_BOILER_EFFICIENCY, baselineEff.mid), gasRateP),
      high: calcBoilerAnnualCost(spaceHeatKwh, dhwKwh, Math.max(MIN_BOILER_EFFICIENCY, baselineEff.low), gasRateP),
    };

    penaltyItems = buildBoilerPenalties(spaceHeatKwh, dhwKwh, gasRateP, baseEff);
  }

  const tariffScenarios = buildTariffScenarios(input);
  const bestCaseRequirements = buildBestCaseRequirements(option, input);
  const { confidence, tighten } = assessConfidence(source, tariffScenario === 'known');

  return {
    annualRunningCostGBP: {
      bestCase: bestCaseCost,
      baseline: baselineCost,
    },
    tariffScenarios,
    bestCaseRequirements,
    penaltyItems,
    confidence,
    tightenConfidence: tighten,
    debug: {
      usedHeatDemandSource: source,
      spaceHeatKwh,
      dhwKwh,
      tariffUsed: tariffScenario,
    },
  };
}
